// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { combineSlices } from '@reduxjs/toolkit';
import { setupStore, createReduxHooks } from '@shared/data-access/utils';
import { environment } from '@shared/environment';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  apiCacheSyncMiddleware,
  syncInstitutionsMiddleware,
} from '@shared/data-access/middlewares';
import { authSlice } from '@shared/data-access/slices/auth';
import { assetsRepositorySlice } from '@shared/data-access/slices/assets-repository';
import { advisoryRepositorySlice } from '@shared/data-access/slices/advisory-repository';
import { documentsRepositorySlice } from '@shared/data-access/slices/documents-repository';
import { illiquidAssetsSlice } from '@shared/data-access/slices/illiquid-assets';
import { assetsTypeSlice } from '@shared/data-access/slices/assets-type';
import { availableParametersSlice } from '@shared/data-access/slices/available-parameters';
import { brokerSlice } from '@shared/data-access/slices/broker';
import { clientsSlice } from '@shared/data-access/slices/clients';
import { copilotBufferSlice } from '@shared/data-access/slices/copilot-buffer';
import { researchSlice } from '@shared/data-access/slices/research';
import { parametersSlice } from '@shared/data-access/slices/parameters';
import { portfolioPositionsSlice } from '@shared/data-access/slices/portfolio-positions';
import { tagManagerSlice } from '@shared/data-access/slices/tag-manager';
import { tagsManagementSlice } from '@shared/data-access/slices/tags-management';
import { traderSlice } from '@shared/data-access/slices/trader';
import { userSlice } from '@shared/data-access/slices/user';
import { workflowSlice } from '@shared/data-access/slices/workflow';
import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import {
  engineApi,
  engineApiV2,
} from '@shared/data-access/api-base/citec-engine-api';
import {
  brokerageApi,
  brokerageApiV2,
} from '@shared/data-access/api-base/citec-brokerage-api';
import { copilotApi } from '@shared/data-access/api-base/citec-copilot-api';
import { auth0Api } from '@shared/data-access/api-base/citec-auth0-api';

export const rootReducer = combineSlices(
  authSlice,
  assetsRepositorySlice,
  advisoryRepositorySlice,
  documentsRepositorySlice,
  illiquidAssetsSlice,
  assetsTypeSlice,
  availableParametersSlice,
  brokerSlice,
  clientsSlice,
  copilotBufferSlice,
  researchSlice,
  parametersSlice,
  portfolioPositionsSlice,
  tagManagerSlice,
  tagsManagementSlice,
  traderSlice,
  userSlice,
  workflowSlice,
  // RTK Apis
  aiApi,
  brokerageApi,
  brokerageApiV2,
  engineApi,
  engineApiV2,
  copilotApi,
  auth0Api,
);

const persistentReducer = persistReducer(
  {
    key: `${environment.APP.NAME}@${environment.APP.VERSION}`,
    version: environment.APP.BUILD_ID,
    storage,
    blacklist: [
      'advisoryRepository',
      'documentsRepository',
      authSlice.reducerPath,
      // APIs should not be persisted
      aiApi.reducerPath,
      brokerageApi.reducerPath,
      brokerageApiV2.reducerPath,
      engineApi.reducerPath,
      engineApiV2.reducerPath,
      copilotApi.reducerPath,
      auth0Api.reducerPath,
    ],
  },
  rootReducer,
);

export const store = setupStore({
  reducer: persistentReducer,
  devTools: {
    trace: true,
    name: `${environment.APP.NAME}@${environment.APP.BUILD_ID}`,
  },
  middlewareOptions: {
    thunk: true,
    immutableCheck: true,
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
  },
  middlewares: [
    aiApi.middleware,
    brokerageApi.middleware,
    brokerageApiV2.middleware,
    engineApi.middleware,
    engineApiV2.middleware,
    copilotApi.middleware,
    auth0Api.middleware,
    apiCacheSyncMiddleware.middleware,
    syncInstitutionsMiddleware.middleware,
  ],
});

export const persistor = persistStore(store);

export const { useAppDispatch, useAppSelector, useAppStore } =
  createReduxHooks<typeof store>();

export type AppStore = typeof store;

export type AppState = ReturnType<AppStore['getState']>;

export type AppDispatch = AppStore['dispatch'];
